import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Routes from '@/modules/routes';
import Icon from '@/components/ui/Icon';
import apiErrorMessage from '../../shared/apiErrors';
import SPAButton from '../../shared/SPAButton';
import SPADialog from '../../shared/SPADialog';
import SPADropdown from '../../shared/SPADropdown';
import { useSPAToast } from '../../shared/SPAToast';
import { LessonSectionRow, useDeleteSection } from './useSections';

interface SectionActionsProps {
  lessonId: number;
  programId: number;
  section: LessonSectionRow;
}

const ErrorLine = ({ children }: { children: React.ReactNode }) => (
  <p className="tw-m-0 tw-text-xs tw-leading-4 tw-text-spa-error" role="alert">{children}</p>
);

// The legacy row's Preview, Make a Copy and Delete as one menu. Preview is a full-page link into
// the student text, Make a Copy opens the routed copy dialog, Delete confirms in place.
const SectionActions = ({ lessonId, programId, section }: SectionActionsProps) => {
  const { full_title: title, id } = section;
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const spaToast = useSPAToast();
  const destroy = useDeleteSection(programId, lessonId);

  // The row unmounts with the cache patch, taking the dialog with it.
  const confirmDelete = () => destroy.mutate(id, {
    onSuccess: () => spaToast.success(`Deleted “${title}”.`),
  });

  const handleDialogChange = (open: boolean) => {
    if (!open) destroy.reset();
    setDeleting(open);
  };

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
      <SPADropdown>
        <SPADropdown.Trigger asChild>
          <SPAButton aria-label={`Actions for ${title}`} size="xs" variant="outline">
            Actions
            <Icon icon="ChevronDown" size={12} />
          </SPAButton>
        </SPADropdown.Trigger>
        <SPADropdown.Content align="end" className="tw-w-44">
          <SPADropdown.Item asChild>
            <a href={Routes.shared_section_path(id, { program_id: programId })}>Preview</a>
          </SPADropdown.Item>
          <SPADropdown.Item onSelect={() => navigate(`${id}/copy`)}>Make a Copy</SPADropdown.Item>
          <SPADropdown.Separator />
          <SPADropdown.Item className="tw-text-spa-error" onSelect={() => setDeleting(true)}>
            Delete
          </SPADropdown.Item>
        </SPADropdown.Content>
      </SPADropdown>

      <SPADialog open={deleting} onOpenChange={handleDialogChange}>
        <SPADialog.Content size="sm">
          <SPADialog.Header>
            <SPADialog.Title>Delete section?</SPADialog.Title>
            <SPADialog.CloseButton />
          </SPADialog.Header>
          <SPADialog.Body className="tw-flex tw-flex-col tw-gap-3">
            <SPADialog.Description>
              This deletes “{title}” with its Student Text, Tutorial, and Notebooks.
            </SPADialog.Description>
            {destroy.isError && (
              <ErrorLine>{apiErrorMessage(destroy.error, 'The section could not be deleted.')}</ErrorLine>
            )}
          </SPADialog.Body>
          <SPADialog.Footer>
            <SPADialog.Close asChild>
              <SPAButton>Cancel</SPAButton>
            </SPADialog.Close>
            <SPAButton disabled={destroy.isLoading} variant="danger" onClick={confirmDelete}>
              Delete
            </SPAButton>
          </SPADialog.Footer>
        </SPADialog.Content>
      </SPADialog>
    </div>
  );
};

export default SectionActions;
